<script lang="ts" setup>
import dayjs from 'dayjs'
import type { ContentTypePortfolio } from '~~/types/storyblok/contentTypes'
import type { MediaImage } from '~~/types/storyblok/media'

const props = defineProps<{
  blok: ContentTypePortfolio
  title: string
}>()

const logo = computed((): MediaImage => {
  return {
    _uid: 'hero',
    component: 'MediaImage',
    asset: props?.blok?.logo,
  }
})

const investedDate = computed(() => {
  return !!props?.blok?.date_invested
    ? dayjs(props.blok.date_invested).format('MMMM YYYY')
    : ''
})

const exitedDate = computed(() => {
  return !!props?.blok?.date_exited
    ? dayjs(props.blok.date_exited).format('MMMM YYYY')
    : ''
})

const hasHeroBlok = computed(() => !!props?.blok?.hero?.length)

// SEO settings
const pageTitle = props.blok?.seo?.title || props?.title || ''
const pageDescription =
  props.blok?.seo?.description || props.blok?.introduction || ''
const pageImage = props.blok?.seo_image?.filename || ''

const { applyMeta } = usePageMetaHelper(useRoute())
applyMeta({
  name: `${props.title} - Portfolio`,
  title: pageTitle,
  description: pageDescription,
  image: pageImage,
})
</script>

<template lang="pug">
article(v-editable="blok")
  StoryblokComponent(
    v-for="item in blok?.hero"
    :key="item._uid"
    :blok="item"
    :title="props.title"
    isMain
  )

  header(
    class="container pb-36 lg:pb-72 text-md max-w-prose px-18 md:px-0 relative z-20"
    :class="hasHeroBlok ? '-mt-12 md:mt-0 md:pt-48 ' : 'pt-120 md:pt-144'"
  )
    PageBreadcrumbs(
      :parent-pages="[{ label: 'Portfolio', value: '/portfolio' }]"
    )

    div(class="flex flex-col md:flex-row gap-36 md:gap-48 items-center")
      div(class="w-180 md:w-144 p-9 shadow-xl shrink-0")
        MediaImage(
          :blok="logo"
          :with-overlay="false"
          :object-cover="true"
          :width="300"
          aspect="square"
          :alt="`Logo for ${props.title}`"
        )

      component(
        :is="!hasHeroBlok ? 'h1' : 'h2'"
        :class="hasHeroBlok ? 'hidden md:block' : '-order-1 md:order-1'"
        class="mb-0"
      ) {{ props.title }}

  div(class="bg-grey-200")
    div(class="content-article pt-48 container text-md flex flex-col items-center")
      StoryblokComponent(
        v-for="item in blok?.blocks"
        :key="item._uid"
        :blok="item"
      )

      div(class="article-meta container max-w-prose flex flex-col px-18 md:px-0 mt-36")
        div(
          v-if="props?.blok?.date_invested"
          class="flex items-center gap-18 text-base text-grey-500 border-b border-grey-300 py-18"
        )
          MaterialSymbol history
          p(class="m-0") Investment date: #[strong {{ investedDate }}]

        div(
          v-if="props?.blok?.date_exited"
          class="flex items-center gap-18 text-base text-grey-500 border-b border-grey-300 py-18"
        )
          MaterialSymbol logout
          p(class="m-0") Exit date: #[strong {{ exitedDate }}]

        div(
          v-if="props?.blok?.url && props?.blok?.url?.linktype === 'url'"
          class="flex items-center gap-18 text-base text-grey-500 border-b border-grey-300 py-18"
        )
          MaterialSymbol link
          NuxtLink(:to="props.blok.url.url" class="m-0") {{ props.blok.url.url }}

      hr(class="mx-auto")
</template>

<style lang="postcss" scoped>
.content-article {
  .blok-rich-text {
    @apply max-w-prose mb-24 px-18 md:px-0;

    & + & {
      @apply mt-36;
    }
  }

  .blok-media {
    @apply max-w-prose-widest mt-24 mb-48;
  }
}
</style>
